'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import {
  Menu,
  X,
  MessageCircle,
  Heart,
  LogOut,
  Crown,
  Settings,
  HelpCircle,
  Home,
  Compass,
  User,
} from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { jsnService } from '@/lib/supabase/jsnService';
import type { UserProfile } from '@/lib/supabase/types';

interface AppNavProps {
  activePage?: 'discover' | 'matches' | 'messages' | 'membership' | 'safety' | 'home' | 'help';
}

export default function AppNav({ activePage }: AppNavProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const desktopUserMenuRef = useRef<HTMLDivElement>(null);
  const mobileUserMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const load = async () => {
      const supabase = createClient();
      const { data: { user } } = await supabase.auth.getUser();

      if (user) {
        const up = await jsnService.getUserProfileById(user.id);
        setUserProfile(up);
      }
    };

    load();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;

      const clickedDesktopMenu = desktopUserMenuRef.current?.contains(target);
      const clickedMobileMenu = mobileUserMenuRef.current?.contains(target);

      if (!clickedDesktopMenu && !clickedMobileMenu) {
        setUserMenuOpen(false);
      }
    };

    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userMenuOpen]);

  const handleLogout = async () => {
    setUserMenuOpen(false);
    setMobileOpen(false);

    const supabase = createClient();
    await supabase.auth.signOut();

    window.location.href = '/';
  };

  const navLinks = [
    { label: 'Home', href: '/home', key: 'home', icon: Home },
    { label: 'Discover', href: '/discover-page', key: 'discover', icon: Compass },
    { label: 'Matches', href: '/matches', key: 'matches', icon: Heart },
    { label: 'Messages', href: '/messages', key: 'messages', icon: MessageCircle },
    { label: 'Membership', href: '/membership', key: 'membership', icon: Crown },
    { label: 'Safety', href: '/safety', key: 'safety', icon: null },
    { label: 'Help & FAQ', href: '/help', key: 'help', icon: HelpCircle },
  ];

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-card/95 backdrop-blur-md border-b border-border h-14">
        <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 h-full flex items-center justify-between">
          <Link href="/home" className="flex items-center gap-2 no-underline flex-shrink-0 min-w-0">
            <AppLogo size={38} />
            <span className="lg:hidden text-sm font-semibold text-foreground whitespace-nowrap tracking-wide leading-tight">
              Jamuhuri Singles Network
            </span>
            <span className="hidden lg:block text-sm font-medium text-muted-foreground/80 whitespace-nowrap tracking-wide">
              Jamuhuri Singles Network
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1 mx-4">
            {navLinks.map(link => (
              <Link
                key={link.key}
                href={link.href}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  activePage === link.key
                    ? 'bg-primary/15 text-primary' :'text-muted-foreground hover:text-foreground hover:bg-muted/30'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-1 flex-shrink-0">
            <Link href="/matches" className="hidden lg:flex relative p-2 text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-muted/30" aria-label="Matches">
              <Heart size={18} />
            </Link>

            <Link href="/messages" className="hidden lg:flex relative p-2 text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-muted/30" aria-label="Messages">
              <MessageCircle size={18} />
            </Link>

            {userProfile ? (
              <>
                {userProfile.membershipTier === 'premium' ? (
                  <span className="hidden lg:flex items-center gap-1 text-xs font-semibold text-primary bg-primary/10 border border-primary/30 px-2 py-1 rounded-full">
                    <Crown size={11} />
                    Premium
                  </span>
                ) : (
                  <Link href="/membership" className="hidden lg:block btn-primary px-3 py-1.5 rounded-lg text-xs font-semibold">
                    Upgrade
                  </Link>
                )}

                {/* Desktop user menu */}
                <div className="hidden lg:block relative" ref={desktopUserMenuRef}>
                  <button
                    onClick={() => setUserMenuOpen(prev => !prev)}
                    className="flex w-9 h-9 rounded-lg items-center justify-center text-white flex-shrink-0 hover:ring-2 hover:ring-primary/60 hover:ring-offset-1 hover:ring-offset-card transition-all focus:outline-none"
                    style={{ background: userProfile.avatarColor || '#750000' }}
                    title="User menu"
                    aria-label="Open user menu"
                    aria-expanded={userMenuOpen}
                    aria-haspopup="true"
                  >
                    <User size={18} strokeWidth={2} />
                  </button>

                  {userMenuOpen && (
                    <div className="absolute right-0 top-full mt-2 w-48 bg-card border border-border rounded-xl shadow-xl z-50 py-1 fade-in">
                      <div className="px-4 py-2.5 border-b border-border">
                        <p className="text-sm font-semibold text-foreground truncate">
                          {userProfile.displayName || 'Member'}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {userProfile.membershipTier === 'premium' ? '✦ Premium Member' : 'Free Member'}
                        </p>
                      </div>

                      <a
                        href="/account-settings"
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-muted/40 transition-colors"
                      >
                        <User size={15} className="text-muted-foreground flex-shrink-0" />
                        Profile
                      </a>

                      <div
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-muted-foreground/50 cursor-not-allowed select-none"
                        title="Coming Soon"
                      >
                        <Settings size={15} className="flex-shrink-0" />
                        <span>Settings</span>
                        <span className="ml-auto text-xs bg-muted/60 text-muted-foreground px-1.5 py-0.5 rounded-full leading-none">
                          Soon
                        </span>
                      </div>

                      <div className="border-t border-border my-1" />

                      <button
                        type="button"
                        onClick={handleLogout}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-400 hover:bg-red-500/10 transition-colors text-left"
                      >
                        <LogOut size={15} className="flex-shrink-0" />
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <Link href="/sign-up-login-screen" className="hidden lg:block btn-primary px-4 py-1.5 rounded-lg text-xs font-semibold ml-1">
                Sign In
              </Link>
            )}

            {/* Mobile user menu */}
            {userProfile && (
              <div className="lg:hidden relative" ref={mobileUserMenuRef}>
                <button
                  onClick={() => setUserMenuOpen(prev => !prev)}
                  className="p-1.5 transition-colors rounded-lg hover:bg-muted/30 focus:outline-none"
                  aria-label="User menu"
                  aria-expanded={userMenuOpen}
                  aria-haspopup="true"
                >
                  <User size={22} strokeWidth={2} style={{ color: '#750000' }} />
                </button>

                {userMenuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-52 bg-card border border-border rounded-xl shadow-xl z-50 py-1 fade-in">
                    <div className="px-4 py-2.5 border-b border-border">
                      <p className="text-sm font-semibold text-foreground truncate">
                        {userProfile.displayName || 'Member'}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {userProfile.membershipTier === 'premium' ? '✦ Premium Member' : 'Free Member'}
                      </p>
                    </div>

                    <a
                      href="/account-settings"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-muted/40 transition-colors"
                    >
                      <User size={15} className="text-muted-foreground flex-shrink-0" />
                      Profile
                    </a>

                    <div
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-muted-foreground/50 cursor-not-allowed select-none"
                      title="Coming Soon"
                    >
                      <Settings size={15} className="flex-shrink-0" />
                      <span>Settings</span>
                      <span className="ml-auto text-xs bg-muted/60 text-muted-foreground px-1.5 py-0.5 rounded-full leading-none">
                        Soon
                      </span>
                    </div>

                    <div className="border-t border-border my-1" />

                    <button
                      type="button"
                      onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-400 hover:bg-red-500/10 transition-colors text-left"
                    >
                      <LogOut size={15} className="flex-shrink-0" />
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            )}

            <button
              className="lg:hidden p-1.5 transition-colors rounded-lg hover:bg-muted/30"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
            >
              {mobileOpen ? (
                <X size={22} strokeWidth={2.5} style={{ color: '#008000' }} />
              ) : (
                <Menu size={22} strokeWidth={2.5} style={{ color: '#008000' }} />
              )}
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div className="lg:hidden bg-card border-t border-border px-4 py-4 fade-in">
            <div className="flex flex-col gap-1">
              {navLinks.map(link => (
                <Link
                  key={link.key}
                  href={link.href}
                  onClick={() => setMobileOpen(false)}
                  className={`px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    activePage === link.key
                      ? 'bg-primary/15 text-primary' :'text-muted-foreground hover:text-foreground hover:bg-muted/30'
                  }`}
                >
                  {link.label}
                </Link>
              ))}

              {userProfile && (
                <>
                  <div className="border-t border-border mt-2 pt-3 flex flex-col gap-1">
                    <div className="flex items-center gap-3 px-3 py-2">
                      <div
                        className="w-9 h-9 rounded-lg flex items-center justify-center text-white flex-shrink-0"
                        style={{ background: userProfile.avatarColor || '#750000' }}
                      >
                        <User size={18} strokeWidth={2} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-foreground truncate">
                          {userProfile.displayName || 'Member'}
                        </p>
                        {userProfile.membershipTier === 'premium' ? (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary">
                            <Crown size={10} />
                            Premium Member
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">Free Member</span>
                        )}
                      </div>
                    </div>

                    <Link
                      href="/matches"
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-colors"
                    >
                      <Heart size={16} />
                      Matches
                    </Link>

                    <Link
                      href="/messages"
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-colors"
                    >
                      <MessageCircle size={16} />
                      Messages
                    </Link>

                    {userProfile.membershipTier !== 'premium' && (
                      <Link
                        href="/membership"
                        onClick={() => setMobileOpen(false)}
                        className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium text-primary hover:bg-primary/10 transition-colors"
                      >
                        <Crown size={16} />
                        Upgrade to Premium
                      </Link>
                    )}

                    <Link
                      href="/account-settings"
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-colors"
                    >
                      <Settings size={16} />
                      Account Settings
                    </Link>

                    <Link
                      href="/help"
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-colors"
                    >
                      <HelpCircle size={16} />
                      Help &amp; FAQ
                    </Link>

                    <button
                      type="button"
                      onClick={handleLogout}
                      className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-colors text-left"
                    >
                      <LogOut size={16} />
                      Sign Out
                    </button>
                  </div>
                </>
              )}

              {!userProfile && (
                <Link
                  href="/sign-up-login-screen"
                  onClick={() => setMobileOpen(false)}
                  className="mt-2 btn-primary px-4 py-2.5 rounded-lg text-sm font-semibold text-center"
                >
                  Sign In
                </Link>
              )}
            </div>
          </div>
        )}
      </header>
    </>
  );
}